/**
 * Simple Dropdown Web Component
 *
 * Generic dropdown UI: text-width button + soft-bordered menu on desktop,
 * bottom sheet on mobile. Emits `simple-dropdown:change` when an option is
 * selected. Consumers wire up their own form submission.
 *
 * Adapted from <custom-dropdown> (product variant selector) without the
 * variant-specific inventory / PubSub / VariantSelects coupling, so it can
 * be reused for sort + country pickers.
 */
if (!customElements.get("simple-dropdown")) {
  customElements.define(
    "simple-dropdown",
    class SimpleDropdown extends HTMLElement {
      static MOBILE_BREAKPOINT = "(max-width: 749px)";

      static scrollLockCount = 0;

      static incrementScrollLock() {
        SimpleDropdown.scrollLockCount++;
        if (SimpleDropdown.scrollLockCount === 1) {
          document.body.style.overflow = "hidden";
        }
      }

      static decrementScrollLock() {
        SimpleDropdown.scrollLockCount = Math.max(
          0,
          SimpleDropdown.scrollLockCount - 1,
        );
        if (SimpleDropdown.scrollLockCount === 0) {
          document.body.style.overflow = "";
        }
      }

      constructor() {
        super();
        this.isOpen = false;
        this.currentIndex = -1;
        this.openedOnMobile = false;
        this.mobileMediaQuery = window.matchMedia(
          SimpleDropdown.MOBILE_BREAKPOINT,
        );
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
      }

      connectedCallback() {
        this.button = this.querySelector("[data-dropdown-button]");
        this.menu = this.querySelector("[data-dropdown-menu]");
        this.selectedText = this.querySelector("[data-selected-text]");
        this.overlay = this.querySelector("[data-dropdown-overlay]");

        if (!this.button || !this.menu) return;

        this.setupEventListeners();
        this.setupKeyboardNav();

        this.mobileMediaQuery.addEventListener(
          "change",
          this.handleViewportChange,
        );
      }

      disconnectedCallback() {
        document.removeEventListener("click", this.handleOutsideClick);
        this.mobileMediaQuery.removeEventListener(
          "change",
          this.handleViewportChange,
        );

        if (this.isOpen && this.openedOnMobile) {
          SimpleDropdown.decrementScrollLock();
        }

        this.isOpen = false;
        this.openedOnMobile = false;
      }

      handleOutsideClick(e) {
        if (this.isOpen && !this.contains(e.target)) {
          this.closeDropdown();
        }
      }

      handleViewportChange(e) {
        if (!this.isOpen) return;
        const nowMobile = e.matches;
        const wasMobile = this.openedOnMobile;

        if (nowMobile && !wasMobile) {
          if (this.overlay) this.overlay.classList.add("is-visible");
          SimpleDropdown.incrementScrollLock();
          this.openedOnMobile = true;
        } else if (!nowMobile && wasMobile) {
          if (this.overlay) this.overlay.classList.remove("is-visible");
          SimpleDropdown.decrementScrollLock();
          this.openedOnMobile = false;
        }
      }

      setupEventListeners() {
        this.button.addEventListener("click", (e) => {
          e.preventDefault();
          this.toggleDropdown();
        });

        this.menu.addEventListener("click", (e) => {
          const optionEl = e.target.closest('[role="option"]');
          if (!optionEl) return;
          if (optionEl.getAttribute("aria-disabled") === "true") return;
          this.selectOption(optionEl);
        });

        document.addEventListener("click", this.handleOutsideClick);

        if (this.overlay) {
          this.overlay.addEventListener("click", () => this.closeDropdown());
        }
      }

      setupKeyboardNav() {
        this.button.addEventListener("keydown", (e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!this.isOpen) this.openDropdown();
            this.navigateOptions(e.key === "ArrowDown" ? 1 : -1);
          } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (this.isOpen) {
              this.selectCurrentOption();
            } else {
              this.toggleDropdown();
            }
          } else if (e.key === "Escape" && this.isOpen) {
            this.closeDropdown();
          }
        });

        this.addEventListener("keydown", (e) => {
          if (!this.isOpen) return;
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            this.navigateOptions(e.key === "ArrowDown" ? 1 : -1);
          } else if (e.key === "Enter") {
            e.preventDefault();
            this.selectCurrentOption();
          } else if (e.key === "Escape") {
            this.closeDropdown();
            this.button.focus();
          }
        });
      }

      navigateOptions(direction) {
        const options = Array.from(
          this.menu.querySelectorAll(
            '[role="option"]:not([aria-disabled="true"])',
          ),
        );
        if (options.length === 0) return;

        this.currentIndex += direction;
        if (this.currentIndex < 0) this.currentIndex = options.length - 1;
        if (this.currentIndex >= options.length) this.currentIndex = 0;

        this.menu.querySelectorAll('[role="option"]').forEach((opt) => {
          opt.classList.remove("custom-dropdown__option--focused");
        });
        options[this.currentIndex].classList.add(
          "custom-dropdown__option--focused",
        );
        options[this.currentIndex].scrollIntoView({ block: "nearest" });
      }

      selectCurrentOption() {
        const options = Array.from(
          this.menu.querySelectorAll(
            '[role="option"]:not([aria-disabled="true"])',
          ),
        );
        if (this.currentIndex >= 0 && this.currentIndex < options.length) {
          this.selectOption(options[this.currentIndex]);
        }
      }

      selectOption(optionEl) {
        const value = optionEl.dataset.optionValue;
        const labelEl = optionEl.querySelector(
          ".custom-dropdown__option-label",
        );
        const label = labelEl ? labelEl.textContent.trim() : value;

        if (this.selectedText) {
          this.selectedText.textContent = label;
        }

        this.menu.querySelectorAll('[role="option"]').forEach((opt) => {
          opt.setAttribute(
            "aria-selected",
            opt === optionEl ? "true" : "false",
          );
        });

        this.dispatchEvent(
          new CustomEvent("simple-dropdown:change", {
            bubbles: true,
            detail: { value, label, optionEl },
          }),
        );

        this.closeDropdown();
      }

      toggleDropdown() {
        if (this.isOpen) {
          this.closeDropdown();
        } else {
          this.openDropdown();
        }
      }

      openDropdown() {
        this.isOpen = true;
        this.openedOnMobile = this.mobileMediaQuery.matches;

        this.button.setAttribute("aria-expanded", "true");
        this.button.classList.add("custom-dropdown__button--open");

        this.menu.removeAttribute("hidden");

        if (this.overlay && this.openedOnMobile) {
          this.overlay.classList.add("is-visible");
        }

        // Force a reflow so the browser registers the un-hidden state
        // before the class lands — otherwise the open transition is skipped.
        this.menu.offsetHeight;
        this.menu.classList.add("is-visible");

        const selectedOption = this.menu.querySelector(
          '[aria-selected="true"]',
        );
        if (selectedOption) {
          const options = Array.from(
            this.menu.querySelectorAll(
              '[role="option"]:not([aria-disabled="true"])',
            ),
          );
          this.currentIndex = options.indexOf(selectedOption);
          selectedOption.classList.add("custom-dropdown__option--focused");
        }

        if (this.openedOnMobile) {
          SimpleDropdown.incrementScrollLock();
        }
      }

      closeDropdown() {
        this.isOpen = false;
        this.button.setAttribute("aria-expanded", "false");
        this.button.classList.remove("custom-dropdown__button--open");
        this.currentIndex = -1;

        this.menu.querySelectorAll('[role="option"]').forEach((opt) => {
          opt.classList.remove("custom-dropdown__option--focused");
        });

        if (this.overlay && this.openedOnMobile) {
          this.overlay.classList.remove("is-visible");
        }
        this.menu.classList.remove("is-visible");

        const animationDuration =
          parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--duration-default",
            ),
          ) || 200;

        setTimeout(() => {
          if (!this.isOpen) this.menu.setAttribute("hidden", "");
        }, animationDuration);

        if (this.openedOnMobile) {
          SimpleDropdown.decrementScrollLock();
        }
        this.openedOnMobile = false;
      }
    },
  );
}

/**
 * Adapter: when a <simple-dropdown> has [data-target-input], pipe its
 * selected value into the input with that id and fire an `input` event
 * on the input — so any surrounding form (e.g. FacetFiltersForm) picks
 * up the change through its existing listener.
 *
 * For the country picker we additionally submit the closest form on
 * change ([data-country-dropdown]) — country switching reloads the page.
 */
document.addEventListener("simple-dropdown:change", (e) => {
  const host = e.target;
  if (!(host instanceof HTMLElement)) return;
  const targetId = host.dataset.targetInput;
  if (!targetId) return;
  const input = document.getElementById(targetId);
  if (!input) return;
  input.value = e.detail.value;
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));

  if (host.hasAttribute("data-country-dropdown")) {
    const form = host.closest("form");
    if (form) form.submit();
  }
});
